import { Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  BILLING_DISPLAY,
  BILLING_VALUES,
  CONTRACT_DISPLAY,
  CONTRACT_VALUES,
  billingFor,
  canonicalPartner,
  isContract,
  normalisePartner,
  type Billing,
  type ContractFormState,
} from '@/config/contract';
import './ContractFields.css';

interface ContractFieldsProps {
  value: ContractFormState;
  onChange: (next: ContractFormState) => void;
  /** The billing the config holds now. On a Partner contract it is the "Billed as" choice. */
  billing: string;
  onBillingChange: (billing: Billing) => void;
  /** The partners named on other clients, for the picker. */
  partnerNames: string[];
  /** Why the contract starts where it does on a new client, e.g. "the account is in NextGen". */
  suggestion?: string;
  /**
   * `select`: the contract dropdown alone, one grid column wide. `details`:
   * what the contract asks for next (a partner and its billing, or trial
   * dates), or nothing. The form puts the details on a row of their own.
   */
  part: 'select' | 'details';
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * The day the person picked, as `yyyy-mm-dd`. It is read from the local date,
 * not the UTC one: in New Zealand a local midnight is still the day before in
 * UTC, which stored the day before the one picked.
 */
const toDateString = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** A stored `yyyy-mm-dd` as a local date, so the picker shows the same day that was stored. */
const fromDateString = (value: string): Date | null => {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

/**
 * The client's contract: one of six, required. A Trial shows its dates, and a
 * Partner shows the partner and how the client is billed. The form saves the
 * contract in the portal's own table and the billing it means in the config.
 */
export function ContractFields({
  value,
  onChange,
  billing,
  onBillingChange,
  partnerNames,
  suggestion,
  part,
}: ContractFieldsProps) {
  const contract = value.contract;
  const set = (patch: Partial<ContractFormState>) => onChange({ ...value, ...patch });
  const typedPartner = normalisePartner(value.partner);
  const knownPartner = !!typedPartner && canonicalPartner(typedPartner, partnerNames) !== typedPartner;
  const newPartner = !!typedPartner && !partnerNames.some((name) => name.toLowerCase() === typedPartner.toLowerCase());
  const savedBilling = contract ? billingFor(contract, billing) : null;

  if (part === 'details' && contract !== 'partner' && contract !== 'trial') return null;

  return (
    <div className="contract-fields">
      {part === 'select' && (
        <Form.Group className="config-field" controlId="config-contract">
          <div className="config-field__head">
            <div className="config-field__title">
              <Form.Label className="config-field__label">Contract type</Form.Label>
              <span className="config-field__default">required</span>
            </div>
          </div>
          <Form.Select
            value={contract}
            isInvalid={!contract}
            onChange={(e) => {
              const next = e.target.value;
              if (!isContract(next)) return;
              set({ contract: next });
              // A partner client starts billed as Credits. The person can change it below.
              if (next === 'partner') onBillingChange('credits');
            }}
          >
            {!contract && (
              <option value="" disabled>
                Choose one (required)
              </option>
            )}
            {CONTRACT_VALUES.map((c) => (
              <option key={c} value={c}>
                {CONTRACT_DISPLAY[c].label}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="config-field__help">
            {contract ? CONTRACT_DISPLAY[contract].hint : 'Every client needs one before the form can save.'}
            {suggestion && contract && <> Suggested because {suggestion}.</>}
            {savedBilling && contract !== 'partner' && <> The config saves it as {BILLING_DISPLAY[savedBilling]}.</>}
          </Form.Text>
        </Form.Group>
      )}

      {part === 'details' && contract === 'partner' && (
        <div className="contract-fields__pair">
          <Form.Group className="config-field" controlId="config-partner">
            <div className="config-field__head">
              <div className="config-field__title">
                <Form.Label className="config-field__label">Partner</Form.Label>
                <span className="config-field__default">required</span>
              </div>
            </div>
            <Form.Control
              list="config-partner-names"
              value={value.partner}
              isInvalid={!typedPartner}
              placeholder="Choose a partner, or type a new one"
              onChange={(e) => set({ partner: e.target.value })}
              onBlur={() => typedPartner && set({ partner: canonicalPartner(typedPartner, partnerNames) })}
            />
            <datalist id="config-partner-names">
              {partnerNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
            <Form.Text className="config-field__help">
              {newPartner
                ? `${typedPartner} is a new partner. It joins the list when you save.`
                : knownPartner
                  ? `Saved as ${canonicalPartner(typedPartner, partnerNames)}, the spelling on the other clients.`
                  : 'The partner the client came through.'}
            </Form.Text>
          </Form.Group>
          <Form.Group className="config-field" controlId="config-partner-billing">
            <div className="config-field__head">
              <div className="config-field__title">
                <Form.Label className="config-field__label">Billed as</Form.Label>
                <span className="config-field__default">default Credits</span>
              </div>
            </div>
            <Form.Select value={billing} onChange={(e) => onBillingChange(e.target.value as Billing)}>
              {BILLING_VALUES.map((b) => (
                <option key={b} value={b}>
                  {BILLING_DISPLAY[b]}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="config-field__help">How this partner client pays.</Form.Text>
          </Form.Group>
        </div>
      )}

      {part === 'details' && contract === 'trial' && (
        <div className="contract-fields__pair">
          <Form.Group className="config-field" controlId="config-trial-start">
            <Form.Label className="config-field__label">Trial start</Form.Label>
            <DatePicker
              id="config-trial-start"
              selected={fromDateString(value.trialStartDate)}
              onChange={(date: Date | null) => set({ trialStartDate: date ? toDateString(date) : '' })}
              dateFormat="yyyy-MM-dd"
              className="form-control"
              wrapperClassName="contract-fields__date"
              placeholderText="Choose a date"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </Form.Group>
          <Form.Group className="config-field" controlId="config-trial-end">
            <Form.Label className="config-field__label">Trial end</Form.Label>
            <DatePicker
              id="config-trial-end"
              selected={fromDateString(value.trialEndDate)}
              onChange={(date: Date | null) => set({ trialEndDate: date ? toDateString(date) : '' })}
              dateFormat="yyyy-MM-dd"
              className="form-control"
              wrapperClassName="contract-fields__date"
              placeholderText="Choose a date"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              minDate={fromDateString(value.trialStartDate) ?? undefined}
            />
          </Form.Group>
        </div>
      )}
    </div>
  );
}

export default ContractFields;
